import { ApiError } from '~/_v3/common/actors/errors';
import { getConf } from '~/_v3/common/utils/auth.utils';
import { getToken, setAuthToken, setToken } from '~/_v3/composables/useToken';
import { RouteKeys } from '~/router/routes.domain';

const baseUrl = import.meta.env.SILAE_EDP_BACKEND_URL;

export type FranceConnectRequest = {
	code: string;
	state: string;
};

export type FranceConnectPersonalInfo = {
	firstName: string;
	lastName: string;
	birthDate: string;
};

export type NotActivatedUser = {
	notActivatedUserToken: string;
	personalInfo: FranceConnectPersonalInfo;
	sub: string;
};

export type User = {
	sub?: string;
	edpUserV2?: {
		loginUrl: string | null;
	};
	userRefreshToken: string;
	userToken: string;
};

export type FranceConnectResponse = {
	user: User | null;
	email: string | null;
	notActivatedUser: NotActivatedUser | null;
};

export const checkFranceConnectCode = async ({
	input: { code, state }
}: {
	input: FranceConnectRequest;
}): Promise<FranceConnectResponse> => {
	const response = await fetch(baseUrl + '/api/v1/auth/code?code=' + encodeURIComponent(code) + '&state=' + encodeURIComponent(state), {
		method: 'GET'
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to login using FranceConnect code: ${response.status}`, errorBody);
	}

	const json = (await response.json()) as FranceConnectResponse;

	const authToken: string | null = response.headers.get('Set-Authorization');
	if (authToken) {
		const payload = JSON.parse(atob(authToken.split('.')[1]));
		if (payload.type === 'JwtPayloadLoggedUser') {
			const routeToRedirectOnSuccess = localStorage.getItem('franceConnectRedirectOnSuccess');
			if (routeToRedirectOnSuccess !== RouteKeys.MERGE) {
				setAuthToken(authToken);
				setToken('franceConnectToken', authToken);
			} else {
				localStorage.setItem('mergeToken', authToken);
			}
		}
	}

	if (json.notActivatedUser) {
		const notActivatedUserToken = json.notActivatedUser.notActivatedUserToken;
		const payload = JSON.parse(atob(notActivatedUserToken.split('.')[1]));
		if (payload.type === 'JwtPayloadLoggedFCNotActivatedUser') {
			setToken('notActivatedFcUserToken', notActivatedUserToken);
		}
	}

	if (json.user && json.user.edpUserV2) {
		const redirectOnSuccess = localStorage.getItem('franceConnectRedirectOnSuccess');
		if (redirectOnSuccess === RouteKeys.HOME) {
			if (json.user.edpUserV2.loginUrl) {
				window.location.replace(json.user.edpUserV2.loginUrl);
			} else {
				const config = await getConf();
				const edpv2url = config.CONF.SILAE_EDP_V2_URL;
				window.location.replace(`${edpv2url}/fc-callback?sub=${json.user.sub}`);
			}
		}
	}

	if (!json.notActivatedUser && json.user && !json.user.userToken && json.user.sub) {
		throw new ApiError(`Failed to login using FranceConnect code: no token returned, the user has a sub, but it's not linked`, {
			sub: json.user.sub
		});
	}

	if (!json.user && !json.notActivatedUser) {
		throw new ApiError(`Failed to login using FranceConnect code: no token returned`, {});
	}

	return json;
};

export const logoutFranceConnect = async () => {
	const authToken = getToken('authToken');
	const response = await fetch(baseUrl + '/api/v1/auth/franceconnect/logout', {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			Authorization: 'Bearer ' + authToken
		}
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to login using FranceConnect code: ${response.status}`, errorBody);
	}

	return response.json();
};
